// frontend/src/components/FinalAssignments.js

import React, { useState, useEffect } from 'react';
import { getFinalAssignmentResults, getCohorts, getTerms } from '../services/api';
import { stringify } from 'csv-stringify/browser/esm';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { notoSansJPFont } from '../fonts/notosansjp-font.js';
import {
  FileText,
  FileSpreadsheet,
  Download,
  Search,
  Building2,
  ArrowLeft,
  Filter,
  Bed
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const FinalAssignments = ({ isPublic = false }) => {
  const [finalAssignmentResults, setFinalAssignmentResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [assignmentsData, cohortsData, termsData] = await Promise.all([
          getFinalAssignmentResults(isPublic ? 'second' : undefined),
          getCohorts(),
          getTerms()
        ]);

        // Sort cohorts by name descending (e.g. 21M, 20M...)
        const sortedCohorts = cohortsData.sort((a, b) => {
          if (a.name > b.name) return -1;
          if (a.name < b.name) return 1;
          return 0;
        });

        setCohorts(sortedCohorts);
        setTerms(termsData);
        if (sortedCohorts.length > 0) {
          setSelectedCohort(sortedCohorts[0].name);
        }

        setFinalAssignmentResults(assignmentsData);
        // Initial filtering will be handled by the other useEffect
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isPublic]);

  useEffect(() => {
    const lowerTerm = searchTerm.toLowerCase();

    // Deep copy and filter
    const filtered = finalAssignmentResults.map(result => ({
      ...result,
      departments: result.departments.map(dept => ({
        ...dept,
        terms: dept.terms.map(term => ({
          ...term,
          users: term.users.filter(user => {
            // 1. Cohort Filter
            if (selectedCohort === 'others') {
              if (user.cohort_name) return false;
            } else if (selectedCohort && user.cohort_name !== selectedCohort) {
              return false;
            }

            // 2. Search Term Filter
            if (!lowerTerm) return true;
            return (
              user.username.toLowerCase().includes(lowerTerm) ||
              user.student_id.toLowerCase().includes(lowerTerm) ||
              result.institution_name.toLowerCase().includes(lowerTerm) ||
              dept.department_name.toLowerCase().includes(lowerTerm)
            );
          })
        }))
      }))
    })).filter(result => {
      // If there is a search term, remove institutions with no matching users
      if (lowerTerm) {
        return result.departments.some(dept =>
          dept.terms.some(term => term.users.length > 0)
        );
      }
      // If no search term (just cohort filter), show all institutions (even if empty)
      return true;
    });

    setFilteredResults(filtered);
  }, [searchTerm, finalAssignmentResults, selectedCohort]);

  const getTermLabel = (termId) => {
    const term = terms.find((item) => item.id === termId);
    return term?.name || `Term ${termId}`;
  };

  const handleExcelExport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'crikura-ii';
      workbook.created = new Date();

      const sheet = workbook.addWorksheet('Final Assignments');
      sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 1 }];

      const baseBorder = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
      const redBorder = {
        top: { style: 'medium', color: { argb: 'FFFF0000' } },
        left: { style: 'medium', color: { argb: 'FFFF0000' } },
        bottom: { style: 'medium', color: { argb: 'FFFF0000' } },
        right: { style: 'medium', color: { argb: 'FFFF0000' } }
      };

      const termLabels = [...Array(7)].map((_, i) => getTermLabel(i + 1));
      const header = ['病院名', '診療科', ...termLabels];
      sheet.addRow(header);

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.height = 24;
      headerRow.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        cell.border = baseBorder;
      });

      sheet.getColumn(1).width = 28;
      sheet.getColumn(2).width = 22;
      for (let i = 3; i <= 9; i += 1) {
        sheet.getColumn(i).width = 20;
      }

      filteredResults.forEach((result) => {
        result.departments.forEach((department) => {
          const row = sheet.addRow([
            result.institution_name,
            department.department_name,
            '', '', '', '', '', '', ''
          ]);
          row.height = 30;
          row.eachCell((cell) => {
            cell.alignment = { vertical: 'top', wrapText: true };
            cell.border = baseBorder;
          });

          department.terms.forEach((term, termIndex) => {
            const cell = row.getCell(3 + termIndex);
            const users = term.users || [];

            if (users.length > 0) {
              const richText = users.map((user, index) => {
                const color = user.sex === 'female' ? 'FFFF0000' : 'FF000000';
                const suffix = index < users.length - 1 ? '\n' : '';
                return {
                  text: `${user.student_id}\n${user.username}${suffix}`,
                  font: { color: { argb: color } }
                };
              });
              cell.value = { richText };
            } else {
              cell.value = '';
            }

            const accommodationAvailable = term.has_accommodation ?? true;
            const needsNoAccommodationBorder =
              accommodationAvailable && users.some((user) => user.accommodation === false);
            if (needsNoAccommodationBorder) {
              cell.border = redBorder;
            }
          });
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'final_assignments.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  };

  const handleCsvExport = () => {
    const header = ['病院名', '科', 'ターム1', 'ターム2', 'ターム3', 'ターム4', 'ターム5', 'ターム6', 'ターム7'];
    const data = [];

    finalAssignmentResults.forEach(result => {
      result.departments.forEach(department => {
        const row = [result.institution_name, department.department_name];
        department.terms.forEach(term => {
          row.push(term.users.map(user => `${user.student_id} ${user.username}`).join('\n'));
        });
        data.push(row);
      });
    });

    const BOM = '\uFEFF';
    stringify([header, ...data], { bom: true, quoted_string: true }, (err, csv) => {
      if (err) {
        console.error('CSV生成エラー:', err);
        return;
      }

      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'final_assignments.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  const handlePdfExport = () => {
    const doc = new jsPDF('l', 'mm', 'a4');

    doc.addFileToVFS('NotoSansJP-Regular.ttf', notoSansJPFont);
    doc.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');

    const header = [['病院名', '科', 'ターム1', 'ターム2', 'ターム3', 'ターム4', 'ターム5', 'ターム6', 'ターム7']];
    const data = [];

    finalAssignmentResults.forEach(result => {
      result.departments.forEach(department => {
        const row = [result.institution_name, department.department_name];
        department.terms.forEach(term => {
          row.push(term.users.map(user => `${user.student_id}\n${user.username}`).join('\n'));
        });
        data.push(row);
      });
    });

    doc.autoTable({
      head: header,
      body: data,
      startY: 20,
      styles: {
        font: 'NotoSansJP',
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 },
        8: { cellWidth: 25 }
      }
    });

    doc.save('final_assignments.pdf');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={isPublic ? "/dashboard" : "/admin-dashboard"} className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600 rounded flex items-center justify-center shadow-sm">
                <Building2 className="text-white" size={18} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isPublic ? '配属状況一覧' : 'FINAL ASSIGNMENTS'}
              </h1>
            </div>
          </div>
          {!isPublic && (
            <div className="flex items-center gap-3">
              <Button
                onClick={handleCsvExport}
                variant="outline"
                className="flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50"
              >
                <FileText size={16} />
                CSV
              </Button>
              <Button
                onClick={handleExcelExport}
                variant="outline"
                className="flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50"
              >
                <FileSpreadsheet size={16} />
                XLSX
              </Button>
              <Button
                onClick={handlePdfExport}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download size={16} />
                PDF
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">

        {/* Filter Card */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          {/* Cohort Tabs */}
          {!isPublic && cohorts.length > 0 && (
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {cohorts.map((cohort) => (
                  <button
                    key={cohort.id}
                    onClick={() => setSelectedCohort(cohort.name)}
                    className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                      ${selectedCohort === cohort.name
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    `}
                  >
                    {cohort.name}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedCohort('others')}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${selectedCohort === 'others'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  その他
                </button>
              </nav>
            </div>
          )}

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-1 max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="病院名、科名、学生名で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all placeholder:text-gray-400"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 ml-auto bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
              <Filter size={14} />
              <span>{filteredResults.length} 病院を表示中</span>
            </div>
          </div>
        </section>

        {error ? (
          <div className="p-8 text-center bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
          >
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="w-[140px] min-w-[120px] font-bold text-gray-700 bg-gray-50 sticky left-0 z-10 border-b border-gray-200">病院名</TableHead>
                    <TableHead className="w-[140px] min-w-[120px] font-bold text-gray-700 bg-gray-50 sticky left-[140px] z-10 border-b border-gray-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">科</TableHead>
                    {[...Array(7)].map((_, i) => (
                      <TableHead key={`term-${i + 1}`} className="font-bold text-gray-700 whitespace-nowrap min-w-[140px] border-b border-gray-200">
                        ターム {i + 1}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                        該当するデータが見つかりませんでした
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredResults.map((result) =>
                      result.departments.map((department) => (
                        <TableRow
                          key={`${result.institution_name}-${department.department_name}`}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <TableCell className="font-medium text-gray-900 sticky left-0 bg-white border-r border-gray-100 align-top">
                            <div className="whitespace-normal leading-tight" title={result.institution_name}>
                              {result.institution_name}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-700 sticky left-[140px] bg-white border-r border-gray-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] align-top">
                            <div className="whitespace-normal leading-tight" title={department.department_name}>
                              {department.department_name}
                            </div>
                          </TableCell>
                          {department.terms.map((term) => (
                            <TableCell key={`term-${term.term_id}`} className="align-top">
                              <div className="space-y-1">
                                {term.users.map((user) => (
                                  <div
                                    key={user.username}
                                    className={`flex items-center gap-1.5 px-1.5 py-1 rounded border overflow-hidden ${user.sex === 'male'
                                      ? 'bg-blue-50 border-blue-100 text-blue-700'
                                      : 'bg-pink-50 border-pink-100 text-pink-700'
                                      }`}
                                  >
                                    <span className="text-[10px] font-mono opacity-70 shrink-0">{user.student_id}</span>
                                    <span className="text-xs font-medium truncate">{user.username}</span>
                                    {user.accommodation && (
                                      <div className="shrink-0 text-amber-500 ml-auto" title="宿泊希望あり">
                                        <Bed size={12} fill="currentColor" fillOpacity={0.2} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {term.users.length === 0 && (
                                  <span className="text-gray-300 text-xs ml-2">-</span>
                                )}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};
export default FinalAssignments;
